import panelManager from '@/core/helpers/panelManager'

const BUILTIN_SETTINGS = {
    alwaysOnTop(vm) {
        if (vm.isCompact || !vm.floatingMode) return null
        return {
            label: vm.$t('common.panelSettings.alwaysOnTop'),
            icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 4v6l-2 4h5v6l1-1 1 1v-6h5l-2-4V4"/><line x1="8" y1="4" x2="16" y2="4"/></svg>',
            action: () => vm.onToggleAlwaysOnTop(),
            checked: vm.alwaysOnTop,
        }
    },
    openOnLoad(vm) {
        if (vm.isCompact || !vm.floatingMode) return null
        return {
            label: vm.$t('common.panelSettings.openOnLoad'),
            icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>',
            action: () => vm.onToggleOpenOnLoad(),
            checked: vm.openOnLoad,
        }
    },
    transparent(vm) {
        return {
            label: vm.$t('common.panelSettings.transparent'),
            icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 3l18 18" opacity="0.4"/><path d="M3 9l12 12" opacity="0.25"/><path d="M9 3l12 12" opacity="0.25"/></svg>',
            action: () => vm.onToggleTransparent(),
            checked: vm.panelTransparent,
        }
    },
    resetWindowSize(vm) {
        if (vm.isCompact) return null
        return {
            label: vm.$t('common.panelSettings.resetWindowSize'),
            icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>',
            action: () => vm.onResetWindowSize(),
        }
    },
    resetInnerLayout(vm) {
        if (vm.isCompact || !vm.panelHasInnerLayout) return null
        return {
            label: vm.$t('common.panelSettings.resetInnerLayout'),
            icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h7v7H3z"/><path d="M14 3h7v7h-7z"/><path d="M3 14h7v7H3z"/><path d="M14 14h7v7h-7z"/></svg>',
            action: () => vm.onResetInnerLayout(),
        }
    },
}

const DEFAULT_SETTINGS = ['alwaysOnTop', 'openOnLoad', 'divider', 'resetWindowSize', 'resetInnerLayout']

export function createPanelMixin(config) {
    const {
        panelId,
        minWidth = 600,
        maxWidth = Infinity,
        minHeight = 400,
        defaults = {},
        dragExcludeSelectors = [],
        hasInnerLayout = true,
        onOpen = null,
        onClose = null,
        onEscape = null,
        onCompactResize = null,
        extraStorageKeys = null,
        onContext = null,
        shortcut = null,
        dividers = [],
        showSettingsInCompact = false,
        settingsItems: configSettingsItems = null,
    } = config

    const effectiveSettingsItems = configSettingsItems || DEFAULT_SETTINGS

    const customSettingsKeys = effectiveSettingsItems.filter(
        entry => typeof entry === 'object' && entry.key && !entry.preset
    )

    const allDragExclude = ['.panel-titlebar-btn', ...dragExcludeSelectors]

    const dividerMethods = {}
    const dividerCleanupKeys = []
    for (const div of dividers) {
        const cap = div.name[0].toUpperCase() + div.name.slice(1)
        const moveKey = `_on${cap}DividerMove`
        const stopKey = `_stop${cap}DividerResize`

        dividerMethods[`start${cap}Resize`] = function (e) {
            const p = this.getPointer(e)
            this[`_${div.name}DivResizing`] = true
            this[`_${div.name}DivStartX`] = p.x
            this[`_${div.name}DivStartW`] = this[div.property]
            document.addEventListener('mousemove', this[moveKey])
            document.addEventListener('mouseup', this[stopKey])
            document.addEventListener('touchmove', this[moveKey], { passive: false })
            document.addEventListener('touchend', this[stopKey])
            e.preventDefault()
        }

        dividerMethods[moveKey] = function (e) {
            if (!this[`_${div.name}DivResizing`]) return
            e.preventDefault()
            const p = this.getPointer(e)
            const dx = p.x - this[`_${div.name}DivStartX`]
            this[div.property] = Math.min(div.max, Math.max(div.min, this[`_${div.name}DivStartW`] + dx))
        }

        dividerMethods[stopKey] = function () {
            this[`_${div.name}DivResizing`] = false
            document.removeEventListener('mousemove', this[moveKey])
            document.removeEventListener('mouseup', this[stopKey])
            document.removeEventListener('touchmove', this[moveKey])
            document.removeEventListener('touchend', this[stopKey])
            this.panelSaveLayoutDebounced()
        }

        dividerCleanupKeys.push(moveKey, stopKey)
    }

    return {
        provide() {
            return { panelVm: this }
        },
        data() {
            const saved = panelManager.loadLayout(panelId)
            const data = {
                visible: false,
                minimized: false,
                animating: null,
                panelZBase: 100000,
                alwaysOnTop: false,
                openOnLoad: panelManager.getOpenOnLoad(panelId),
                floatingMode: saved.floatingMode ?? defaults.floatingMode ?? false,
                panelX: saved.x ?? defaults.x ?? null,
                panelY: saved.y ?? defaults.y ?? null,
                panelWidth: Math.min(maxWidth, saved.w || defaults.width || 1100),
                panelHeight: saved.h || defaults.height || 700,
                sidebarWidth: saved.sidebarWidth || defaults.sidebarWidth || 200,

                isDragging: false,
                dragStartX: 0,
                dragStartY: 0,
                dragStartPanelX: 0,
                dragStartPanelY: 0,

                isResizing: false,
                resizeDir: null,
                resizeStartX: 0,
                resizeStartY: 0,
                resizeStartRect: null,

                isDividerResizing: false,
                dividerStartX: 0,
                dividerStartWidth: 0,

                panelTransparent: saved.panelTransparent ?? (defaults.transparent ?? false),
                shakeHidden: false,

                saveStorageTimer: null,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
            }
            for (const div of dividers) {
                data[div.property] = saved[div.property] || defaults[div.property] || div.min
            }
            for (const cs of customSettingsKeys) {
                data[cs.key] = saved[cs.key] ?? cs.default
            }
            return data
        },

        computed: {
            isCompact() {
                return this.windowWidth < 700
            },
            panelHasInnerLayout() {
                return hasInnerLayout
            },
            canAutoOpen() {
                return this.floatingMode && !this.isCompact
            },
            showSettingsBtn() {
                return !this.isCompact || showSettingsInCompact
            },
            _panelSettingsItems() {
                const result = []
                for (const entry of effectiveSettingsItems) {
                    if (entry === 'divider') { result.push({ type: 'divider' }); continue }

                    if (typeof entry === 'string') {
                        const factory = BUILTIN_SETTINGS[entry]
                        if (!factory) continue
                        const item = factory(this)
                        if (item) result.push(item)
                        continue
                    }

                    if (entry.preset) {
                        const factory = BUILTIN_SETTINGS[entry.preset]
                        if (!factory) continue
                        const base = factory(this)
                        if (!base) continue
                        const { preset: _, ...overrides } = entry
                        if (overrides.label) overrides.label = this.$t(overrides.label)
                        result.push({ ...base, ...overrides })
                        continue
                    }

                    if (typeof entry.visible === 'function' && !entry.visible(this)) continue

                    const item = { label: this.$t(entry.label), icon: entry.icon }
                    if (entry.action) {
                        item.action = entry.action
                        if ('checked' in entry) item.checked = typeof entry.checked === 'function' ? entry.checked(this) : entry.checked
                    } else if (entry.key) {
                        item.action = () => { this[entry.key] = !this[entry.key]; this.panelSaveLayoutDebounced() }
                        item.checked = !!this[entry.key]
                    }

                    result.push(item)
                }

                while (result.length && result[0].type === 'divider') result.shift()
                while (result.length && result[result.length - 1].type === 'divider') result.pop()
                for (let i = result.length - 1; i > 0; i--) {
                    if (result[i].type === 'divider' && result[i - 1].type === 'divider') result.splice(i, 1)
                }

                return result
            },
            panelStyle() {
                const ww = this.windowWidth
                const wh = this.windowHeight

                if (this.isCompact) {
                    return {
                        width: ww + 'px',
                        height: wh + 'px',
                        left: '0px',
                        top: '0px',
                    }
                }

                let w = this.panelWidth
                let h = this.panelHeight

                if (!this.floatingMode) {
                    w = Math.min(w, Math.floor(ww * 0.95))
                    h = Math.min(h, Math.floor(wh * 0.9))
                }

                let x, y
                if (this.floatingMode) {
                    x = Math.round(this.panelX ?? 100)
                    y = Math.round(this.panelY ?? 50)
                } else {
                    x = Math.round((ww - w) / 2)
                    y = Math.round((wh - h) / 2)
                }

                return {
                    width: w + 'px',
                    height: h + 'px',
                    left: x + 'px',
                    top: y + 'px',
                }
            },
        },

        methods: {
            open() {
                if (this.minimized) {
                    this.restore()
                    return
                }
                if (this.visible) {
                    if (this.shakeHidden) {
                        this.shakeHidden = false
                        panelManager.clearShakeHidden(panelId)
                    }
                    return
                }
                this.visible = true
                this.animating = 'open'
                setTimeout(() => { this.animating = null }, 250)
                this.windowWidth = window.innerWidth
                this.windowHeight = window.innerHeight
                if (!this.floatingMode) {
                    document.body.style.overflow = 'hidden'
                }
                this.alwaysOnTop = panelManager.register(panelId, (zBase) => { this.panelZBase = zBase })
                this.fitToScreen()
                document.addEventListener('keydown', this.onKeydown)
                window.addEventListener('resize', this.onWindowResize)
                this.$nextTick(() => {
                    if (onOpen) onOpen(this)
                })
            },
            close() {
                if (onClose) onClose(this)
                if (this.shakeHidden) {
                    this.shakeHidden = false
                    panelManager.clearShakeHidden(panelId)
                }
                this.minimized = false
                document.body.style.overflow = ''
                panelManager.unregister(panelId)
                this.animating = 'close'
                setTimeout(() => {
                    this.visible = false
                    this.animating = null
                }, 200)
                document.removeEventListener('keydown', this.onKeydown)
                window.removeEventListener('resize', this.onWindowResize)
            },
            toggle() {
                if (this.minimized) {
                    this.restore()
                    return
                }
                if (this.visible && this.shakeHidden) {
                    this.shakeHidden = false
                    panelManager.clearShakeHidden(panelId)
                    return
                }
                if (this.visible) this.close()
                else this.open()
            },
            minimize() {
                if (!this.visible || this.minimized) return

                const panelEl = this.$refs.panelFrame?.$refs.panelContent
                if (!panelEl) return

                const dockItem = document.querySelector(`[data-dock-panel="${panelId}"]`)
                let tx = 0, ty = window.innerHeight * 0.6, sx = 0.05, sy = 0.05
                if (dockItem) {
                    const dr = dockItem.getBoundingClientRect()
                    const pr = panelEl.getBoundingClientRect()
                    tx = (dr.left + dr.width / 2) - (pr.left + pr.width / 2)
                    ty = (dr.top + dr.height / 2) - (pr.top + pr.height / 2)
                    sx = Math.max(0.02, dr.width / pr.width)
                    sy = Math.max(0.02, dr.height / pr.height)
                }

                this._genieTarget = { tx, ty, sx, sy }
                this.animating = 'minimize'

                requestAnimationFrame(() => {
                    panelEl.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.45s ease-in, border-radius 0.5s'
                    panelEl.style.transform = `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`
                    panelEl.style.opacity = '0'
                    panelEl.style.borderRadius = '20px'
                })

                setTimeout(() => {
                    panelEl.style.visibility = 'hidden'
                    panelEl.style.transition = ''
                    panelEl.style.transform = ''
                    panelEl.style.opacity = ''
                    panelEl.style.borderRadius = ''
                    this.minimized = true
                    this.animating = null
                }, 550)
            },
            restore() {
                if (!this.minimized) {
                    if (!this.visible) this.open()
                    return
                }

                this.minimized = false
                this.animating = 'restore'

                this.$nextTick(() => {
                    const panelEl = this.$refs.panelFrame?.$refs.panelContent
                    if (!panelEl) { this.animating = null; return }

                    const target = this._genieTarget || { tx: 0, ty: window.innerHeight * 0.5, sx: 0.05, sy: 0.05 }

                    panelEl.style.visibility = ''
                    panelEl.style.transition = 'none'
                    panelEl.style.transform = `translate(${target.tx}px, ${target.ty}px) scale(${target.sx}, ${target.sy})`
                    panelEl.style.opacity = '0'

                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            panelEl.style.transition = 'transform 0.4s cubic-bezier(0, 0, 0.2, 1), opacity 0.35s ease-out, border-radius 0.4s'
                            panelEl.style.transform = ''
                            panelEl.style.opacity = ''
                            panelEl.style.borderRadius = ''
                        })
                    })

                    setTimeout(() => {
                        panelEl.style.transition = ''
                        this.animating = null
                    }, 420)
                })

                panelManager.bringToFront(panelId)
            },
            onContext(context) {
                if (onContext) this.$nextTick(() => onContext(this, context))
            },

            onKeydown(e) {
                if (e.key !== 'Escape') return
                if (this.$refs.panelFrame?.isSettingsMenuOpen()) {
                    this.$refs.panelFrame.closeSettingsMenu()
                    return
                }
                if (onEscape) {
                    const handled = onEscape(this, e)
                    if (handled) return
                }
                if (!this.floatingMode) this.close()
            },

            fitToScreen() {
                this.windowWidth = window.innerWidth
                this.windowHeight = window.innerHeight
                if (this.isCompact) {
                    if (onCompactResize) onCompactResize(this)
                } else if (this.floatingMode) {
                    const fits = this.panelX >= 0
                        && this.panelY >= 0
                        && (this.panelX + this.panelWidth) <= this.windowWidth
                        && (this.panelY + this.panelHeight) <= this.windowHeight
                    if (!fits) {
                        if (this.panelWidth > this.windowWidth) this.panelWidth = Math.min(maxWidth, Math.max(minWidth, this.windowWidth - 20))
                        if (this.panelHeight > this.windowHeight) this.panelHeight = Math.max(minHeight, this.windowHeight - 20)
                        this.panelX = Math.round((this.windowWidth - this.panelWidth) / 2)
                        this.panelY = Math.round((this.windowHeight - this.panelHeight) / 2)
                    }
                }
            },
            onWindowResize() {
                this.fitToScreen()
            },

            toggleFloatingMode() {
                if (this.shakeHidden) {
                    this.shakeHidden = false
                    panelManager.clearShakeHidden(panelId)
                }
                this.floatingMode = !this.floatingMode
                if (this.floatingMode) {
                    document.body.style.overflow = ''
                    if (this.panelX === null || this.panelY === null) {
                        this.panelX = Math.round(Math.max(50, (window.innerWidth - this.panelWidth) / 2))
                        this.panelY = Math.round(Math.max(30, (window.innerHeight - this.panelHeight) / 2))
                    }
                } else {
                    document.body.style.overflow = 'hidden'
                }
                panelManager.notifyFloatingChanged()
                this.panelSaveLayoutDebounced()
            },

            onPanelFocus() {
                if (this.floatingMode) panelManager.bringToFront(panelId)
            },
            openSettingsMenu(event) {
                if (!this.showSettingsBtn) return
                this.$refs.panelFrame?.openSettingsMenu(event)
            },
            openSettingsMenuFromBtn(event) {
                this.$refs.panelFrame?.openSettingsMenu(event)
            },
            onToggleTransparent() {
                this.panelTransparent = !this.panelTransparent
                this.panelSaveLayoutDebounced()
            },
            onToggleAlwaysOnTop() {
                this.alwaysOnTop = !this.alwaysOnTop
                panelManager.setAlwaysOnTop(panelId, this.alwaysOnTop)
            },
            onToggleOpenOnLoad() {
                this.openOnLoad = !this.openOnLoad
                panelManager.setOpenOnLoad(panelId, this.openOnLoad)
            },
            onResetWindowSize() {
                this.panelWidth = Math.min(maxWidth, defaults.width || 1100)
                this.panelHeight = defaults.height || 700
                this.panelX = Math.round((window.innerWidth - this.panelWidth) / 2)
                this.panelY = Math.round((window.innerHeight - this.panelHeight) / 2)
                this.panelSaveLayoutDebounced()
            },
            onResetInnerLayout() {
                this.sidebarWidth = defaults.sidebarWidth || 200
                for (const div of dividers) {
                    this[div.property] = defaults[div.property] || div.min
                }
                this.panelSaveLayoutDebounced()
            },

            setShakeHidden(value) {
                this.shakeHidden = value
            },

            getPointer(e) {
                if (e.touches && e.touches.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY }
                return { x: e.clientX, y: e.clientY }
            },

            startDrag(e) {
                if (!this.floatingMode) return
                for (const sel of allDragExclude) {
                    if (e.target.closest(sel)) return
                }
                const p = this.getPointer(e)
                this.isDragging = true
                this.dragStartX = p.x
                this.dragStartY = p.y
                this.dragStartPanelX = this.panelX ?? 0
                this.dragStartPanelY = this.panelY ?? 0
                document.addEventListener('mousemove', this.onDrag)
                document.addEventListener('mouseup', this.stopDrag)
                document.addEventListener('touchmove', this.onDrag, { passive: false })
                document.addEventListener('touchend', this.stopDrag)
                e.preventDefault()
                this._shakeXs = [{ x: p.x, t: Date.now() }]
                this._shakeReversals = 0
                this._shakeLastDir = 0
                this._shakePeakX = p.x
                this._shakeFirstReversalT = 0
                this._shakeTriggered = false
            },
            onDrag(e) {
                if (!this.isDragging) return
                e.preventDefault()
                const p = this.getPointer(e)
                this.panelX = this.dragStartPanelX + (p.x - this.dragStartX)
                this.panelY = this.dragStartPanelY + (p.y - this.dragStartY)
                this.panelX = Math.max(0, Math.min(this.panelX, window.innerWidth - 100))
                this.panelY = Math.max(0, Math.min(this.panelY, window.innerHeight - 50))

                if (this._shakeTriggered) return
                const now = Date.now()
                this._shakeXs.push({ x: p.x, t: now })
                const cutoff = now - 600
                while (this._shakeXs.length > 1 && this._shakeXs[0].t < cutoff) this._shakeXs.shift()

                const prev = this._shakeXs[this._shakeXs.length - 2]
                if (!prev) return
                const dx = p.x - prev.x
                if (Math.abs(dx) < 3) return
                const dir = dx > 0 ? 1 : -1
                if (this._shakeReversals > 0 && now - this._shakeFirstReversalT > 800) {
                    this._shakeReversals = 0
                    this._shakeFirstReversalT = 0
                }
                if (this._shakeLastDir !== 0 && dir !== this._shakeLastDir) {
                    const swing = Math.abs(p.x - this._shakePeakX)
                    if (swing >= 40) {
                        this._shakeReversals++
                        if (this._shakeReversals === 1) this._shakeFirstReversalT = now
                        this._shakePeakX = p.x
                    }
                } else {
                    if (this._shakeLastDir === 0) this._shakePeakX = p.x
                }
                this._shakeLastDir = dir
                if (this._shakeReversals >= 4) {
                    panelManager.shakeToggleOtherPanels(panelId)
                    this._shakeTriggered = true
                    this._shakeReversals = 0
                    this._shakeXs = []
                    this._shakeLastDir = 0
                    this._shakePeakX = 0
                    this._shakeFirstReversalT = 0
                    setTimeout(() => { this._shakeTriggered = false }, 600)
                }
            },
            stopDrag() {
                this.isDragging = false
                document.removeEventListener('mousemove', this.onDrag)
                document.removeEventListener('mouseup', this.stopDrag)
                document.removeEventListener('touchmove', this.onDrag)
                document.removeEventListener('touchend', this.stopDrag)
                this._shakeXs = null
                this._shakeReversals = 0
                this._shakeLastDir = 0
                this._shakePeakX = 0
                this._shakeFirstReversalT = 0
                this.panelSaveLayoutDebounced()
            },

            startResize(e, dir) {
                const p = this.getPointer(e)
                const panelEl = this.$refs.panelFrame?.$refs.panelContent
                if (panelEl) {
                    const actualH = Math.round(panelEl.getBoundingClientRect().height)
                    if (actualH !== this.panelHeight) {
                        this.panelHeight = Math.max(minHeight, actualH)
                    }
                }
                this.isResizing = true
                this.resizeDir = dir
                this.resizeStartX = p.x
                this.resizeStartY = p.y
                this.resizeStartRect = {
                    x: this.panelX ?? 0,
                    y: this.panelY ?? 0,
                    w: this.panelWidth,
                    h: this.panelHeight,
                }
                document.addEventListener('mousemove', this.onResize)
                document.addEventListener('mouseup', this.stopResize)
                document.addEventListener('touchmove', this.onResize, { passive: false })
                document.addEventListener('touchend', this.stopResize)
                e.preventDefault()
            },
            onResize(e) {
                if (!this.isResizing) return
                e.preventDefault()
                const p = this.getPointer(e)
                const dx = p.x - this.resizeStartX
                const dy = p.y - this.resizeStartY
                const r = this.resizeStartRect
                let x = r.x, y = r.y, w = r.w, h = r.h

                if (this.resizeDir.includes('e')) w = Math.min(maxWidth, Math.max(minWidth, r.w + dx))
                if (this.resizeDir.includes('w')) { w = Math.min(maxWidth, Math.max(minWidth, r.w - dx)); x = r.x + r.w - w }
                if (this.resizeDir.includes('s')) h = Math.max(minHeight, r.h + dy)
                if (this.resizeDir.includes('n')) { h = Math.max(minHeight, r.h - dy); y = r.y + r.h - h }

                this.panelX = x
                this.panelY = y
                this.panelWidth = w
                this.panelHeight = h
            },
            stopResize() {
                this.isResizing = false
                document.removeEventListener('mousemove', this.onResize)
                document.removeEventListener('mouseup', this.stopResize)
                document.removeEventListener('touchmove', this.onResize)
                document.removeEventListener('touchend', this.stopResize)
                this.panelSaveLayoutDebounced()
            },

            startSidebarResize(e) {
                const p = this.getPointer(e)
                this.isDividerResizing = true
                this.dividerStartX = p.x
                this.dividerStartWidth = this.sidebarWidth
                document.addEventListener('mousemove', this.onSidebarDividerResize)
                document.addEventListener('mouseup', this.stopSidebarDividerResize)
                document.addEventListener('touchmove', this.onSidebarDividerResize, { passive: false })
                document.addEventListener('touchend', this.stopSidebarDividerResize)
                e.preventDefault()
            },
            onSidebarDividerResize(e) {
                if (!this.isDividerResizing) return
                e.preventDefault()
                const p = this.getPointer(e)
                const dx = p.x - this.dividerStartX
                const minSidebar = config.minSidebar || 150
                const maxSidebar = config.maxSidebar || 280
                this.sidebarWidth = Math.min(maxSidebar, Math.max(minSidebar, this.dividerStartWidth + dx))
            },
            stopSidebarDividerResize() {
                this.isDividerResizing = false
                document.removeEventListener('mousemove', this.onSidebarDividerResize)
                document.removeEventListener('mouseup', this.stopSidebarDividerResize)
                document.removeEventListener('touchmove', this.onSidebarDividerResize)
                document.removeEventListener('touchend', this.stopSidebarDividerResize)
                this.panelSaveLayoutDebounced()
            },

            panelLoadLayout() {
                return panelManager.loadLayout(panelId)
            },
            panelSaveLayout() {
                const data = {
                    floatingMode: this.floatingMode,
                    x: this.panelX,
                    y: this.panelY,
                    w: this.panelWidth,
                    h: this.panelHeight,
                    sidebarWidth: this.sidebarWidth,
                    panelTransparent: this.panelTransparent,
                }
                for (const div of dividers) {
                    data[div.property] = this[div.property]
                }

                for (const cs of customSettingsKeys) {
                    data[cs.key] = this[cs.key]
                }

                if (extraStorageKeys) {
                    Object.assign(data, extraStorageKeys(this))
                }
                panelManager.saveLayout(panelId, data)
            },
            panelSaveLayoutDebounced() {
                clearTimeout(this.saveStorageTimer)
                this.saveStorageTimer = setTimeout(() => this.panelSaveLayout(), 300)
            },

            ...dividerMethods,
        },

        mounted() {
            panelManager.mountPanel(panelId, this)
            if (shortcut) panelManager.registerShortcut(panelId, shortcut)
        },
        beforeUnmount() {
            panelManager.unmountPanel(panelId)
            panelManager.unregister(panelId)
            document.removeEventListener('keydown', this.onKeydown)
            window.removeEventListener('resize', this.onWindowResize)
            clearTimeout(this.saveStorageTimer)
            for (const key of dividerCleanupKeys) {
                document.removeEventListener('mousemove', this[key])
                document.removeEventListener('mouseup', this[key])
                document.removeEventListener('touchmove', this[key])
                document.removeEventListener('touchend', this[key])
            }
        },
    }
}
